import { SageContactDetails, UpsertSageContactDetails } from '../../types/sage';
import type { ApiErrorCode } from '../../types/api';
import { getFriendlyErrorMessage } from '../../utils/errorUtils';

/** Shared sage/address form fields -- use with intersection types in dialog form data. */
export interface SageFormFields {
  sageName: string;
  taxReference: string;
  postalAddress01: string;
  postalAddress02: string;
  postalAddress03: string;
  postalAddress04: string;
  postalAddress05: string;
  deliveryAddress01: string;
  deliveryAddress02: string;
  deliveryAddress03: string;
  deliveryAddress04: string;
  deliveryAddress05: string;
}

export const EMPTY_SAGE_FIELDS: SageFormFields = {
  sageName: '',
  taxReference: '',
  postalAddress01: '',
  postalAddress02: '',
  postalAddress03: '',
  postalAddress04: '',
  postalAddress05: '',
  deliveryAddress01: '',
  deliveryAddress02: '',
  deliveryAddress03: '',
  deliveryAddress04: '',
  deliveryAddress05: '',
};

/** Build UpsertSageContactDetails from flat form fields. Returns undefined if all fields are empty. */
export function buildSageDetails(fields: SageFormFields): UpsertSageContactDetails | undefined {
  const hasAnySageField = fields.sageName || fields.taxReference ||
    fields.postalAddress01 || fields.postalAddress02 || fields.postalAddress03 ||
    fields.postalAddress04 || fields.postalAddress05 ||
    fields.deliveryAddress01 || fields.deliveryAddress02 || fields.deliveryAddress03 ||
    fields.deliveryAddress04 || fields.deliveryAddress05;

  if (!hasAnySageField) return undefined;

  return {
    sageName: fields.sageName || undefined,
    taxReference: fields.taxReference || undefined,
    postalAddress01: fields.postalAddress01 || undefined,
    postalAddress02: fields.postalAddress02 || undefined,
    postalAddress03: fields.postalAddress03 || undefined,
    postalAddress04: fields.postalAddress04 || undefined,
    postalAddress05: fields.postalAddress05 || undefined,
    deliveryAddress01: fields.deliveryAddress01 || undefined,
    deliveryAddress02: fields.deliveryAddress02 || undefined,
    deliveryAddress03: fields.deliveryAddress03 || undefined,
    deliveryAddress04: fields.deliveryAddress04 || undefined,
    deliveryAddress05: fields.deliveryAddress05 || undefined,
  };
}

/**
 * Map a SageContactDetails object (from an entity) to flat form fields.
 * Returns a SageFormFields object suitable for spreading into dialog form state.
 */
export function mapSageDetailsToFormFields(sageDetails?: SageContactDetails | null): SageFormFields {
  if (!sageDetails) return { ...EMPTY_SAGE_FIELDS };

  return {
    sageName: sageDetails.sageName || '',
    taxReference: sageDetails.taxReference || '',
    postalAddress01: sageDetails.postalAddress01 || '',
    postalAddress02: sageDetails.postalAddress02 || '',
    postalAddress03: sageDetails.postalAddress03 || '',
    postalAddress04: sageDetails.postalAddress04 || '',
    postalAddress05: sageDetails.postalAddress05 || '',
    deliveryAddress01: sageDetails.deliveryAddress01 || '',
    deliveryAddress02: sageDetails.deliveryAddress02 || '',
    deliveryAddress03: sageDetails.deliveryAddress03 || '',
    deliveryAddress04: sageDetails.deliveryAddress04 || '',
    deliveryAddress05: sageDetails.deliveryAddress05 || '',
  };
}

/** Shape of server validation error entries returned by the API (legacy/non-envelope endpoints). */
interface ServerFieldError {
  field?: string;
  message: string;
}

/** Shape of an Axios-like error with optional response data (pre-interceptor-normalization). */
interface AxiosLikeError {
  response?: {
    data?: {
      message?: string;
      errors?: ServerFieldError[] | string[];
      errorContext?: Record<string, unknown>;
    };
  };
  message?: string;
}

/** Shape of the interceptor-normalized envelope error (post-PR2). */
interface NormalizedErrorLike {
  errorCode?: ApiErrorCode | null;
  errorContext?: Record<string, unknown> | null;
  errors?: string[];
  httpStatus?: number;
  message?: string;
}

function isServerFieldError(entry: unknown): entry is ServerFieldError {
  return typeof entry === 'object' && entry !== null && 'message' in entry
    && typeof (entry as { message: unknown }).message === 'string';
}

/**
 * Extract field-level errors from either:
 *   - Legacy axios shape: `response.data.errors = [{field, message}]`
 *   - Normalized envelope: `errorContext.fieldErrors = {camelCaseField: message}`
 *
 * Returns camelCase keys suitable for MUI form error state. Returns `null` when
 * the error payload has no recognisable field-level structure (caller should
 * leave existing form errors untouched).
 */
function extractFieldErrors(error: unknown): Record<string, string> | null {
  if (typeof error !== 'object' || error === null) return null;

  // Normalized envelope: errorContext.fieldErrors is the agreed structured slot
  const normalized = error as NormalizedErrorLike;
  const ctx = normalized.errorContext;
  if (ctx && typeof ctx.fieldErrors === 'object' && ctx.fieldErrors !== null) {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(ctx.fieldErrors as Record<string, unknown>)) {
      if (typeof value === 'string') {
        result[key.charAt(0).toLowerCase() + key.slice(1)] = value;
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  }

  // Legacy axios shape: response.data.errors = [{field, message}, ...]
  const axios = error as AxiosLikeError;
  const rawErrors = axios.response?.data?.errors;
  if (Array.isArray(rawErrors)) {
    const result: Record<string, string> = {};
    for (const entry of rawErrors) {
      if (isServerFieldError(entry) && entry.field) {
        result[entry.field.charAt(0).toLowerCase() + entry.field.slice(1)] = entry.message;
      }
    }
    // Empty object is still a signal that the server responded with an errors array
    // (possibly empty) — preserve existing test behaviour where setErrors({}) is called.
    return result;
  }

  return null;
}

/**
 * Map server error responses to field-level form errors and display a snackbar.
 *
 * Handles both error shapes:
 * 1. Legacy axios shape (`err.response.data.{message,errors}`) — still emitted by endpoints
 *    that don't throw an envelope-aware exception.
 * 2. Post-PR2 normalized shape (`NormalizedApiError` with `errorCode`/`errorContext`/`message`)
 *    produced by the response interceptor for any `ApiResponse.errorCode` failure.
 *
 * Snackbar copy uses `getFriendlyErrorMessage` so known `errorCode`s (e.g. `SAGE_VALIDATION_FAILED`)
 * surface tailored copy instead of raw backend text.
 */
export function mapServerErrorsToFormErrors(
  error: unknown,
  setErrors: (errors: Record<string, string>) => void,
  enqueueSnackbar: (message: string, options?: { variant: string }) => void,
  entityName: string,
): void {
  const fallback = `Failed to save ${entityName}`;
  const message = getFriendlyErrorMessage(error, fallback);
  enqueueSnackbar(message, { variant: 'error' });

  const fieldErrors = extractFieldErrors(error);
  if (fieldErrors !== null) {
    setErrors(fieldErrors);
  }
}

/**
 * Generic field-change handler for dialog forms.
 * Updates the specified field in formData and clears its validation error.
 *
 * Returns new formData and errors objects (immutable update pattern).
 */
export function createFieldChangeHandler<T extends object>(
  setFormData: React.Dispatch<React.SetStateAction<T>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
) {
  return (field: keyof T & string, value: T[keyof T]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (prev[field]) {
        const next = { ...prev };
        next[field] = '';
        return next;
      }
      return prev;
    });
  };
}
